import { useEffect, useState, useEffectEvent } from 'react'
import { useLocation } from 'react-router'
import { usePromise } from '~/shared/hooks/promiseState'
import {
  userPeopleDemographics,
  user,
  type UserPeopleDemographicsListResponse,
} from '@fireflyhealth/api'
import { formatPhoneNumber } from '~/shared/lib/displayText'
import { asArrayOf, isFiniteNumber, parseJson } from '~/shared/lib/guards'

const MAX_RECENTLY_VIEWED = 20

const RECENTLY_VIEWED_PEOPLE_KEY = 'RECENTLY_VIEWED_PEOPLE_KEY_V2'

// Using snake_case to match the API response format from @fireflyhealth/api
export type PersonDemographics = UserPeopleDemographicsListResponse[number]

/**
 * A person shaped into a react-select option: the search adds the `value`/`label`
 * the control needs, and resolves `id` to the user account when there is one.
 */
export type PersonSearchOption = PersonDemographics & {
  value: number
  label: string
  id: number
}

/**
 * The stored ids reach an `id__in` request and are sliced by the caller, so a stored value that is
 * not a list of numbers reads as an empty history rather than being trusted this far.
 */
const getViewedPeople = (): number[] => {
  const stored = localStorage.getItem(RECENTLY_VIEWED_PEOPLE_KEY)
  if (!stored) return []
  return asArrayOf(parseJson(stored), isFiniteNumber) ?? []
}

// Sets array of numbers as a csv
const saveViewedPeople = (ids: number[]): void => {
  localStorage.setItem(RECENTLY_VIEWED_PEOPLE_KEY, JSON.stringify(ids))
}

/** The literal words to highlight in the name and in the formatted phone number. */
export const getSearchHighlights = (
  searchValue: string,
  name: string | null | undefined,
  phoneNumber: string | null | undefined,
): [string[], string[]] => {
  const search = searchValue.toLowerCase()
  const isNameMatch = (name ?? '').toLowerCase().includes(search)
  const isPhoneMatch = (phoneNumber ?? '').includes(searchValue)
  const nameHighlights = isNameMatch ? [search] : []
  const phoneHighlights = isPhoneMatch ? [formatPhoneNumber(searchValue)] : []
  return [nameHighlights, phoneHighlights]
}

/**
 * LocalStorage-powered recently viewed people list
 * - Loads list of person ids on init and fetches people to store in state
 * - Backs up list of ids in localStorage when window closes
 * - Removes old people from list based on MAX_RECENTLY_VIEWED count
 *
 * The person data stored in state are a limited set of just demographic data
 * The set of complete people data is hydrated after selecting a patient/person
 */
export const useRecentlyViewedPeople = (): PersonDemographics[] => {
  // The list is powered by Person IDs
  // The order should match the order in which the People were viewed
  // Element 0 => most-recently viewed
  const [ids, setIds] = useState<number[]>(getViewedPeople())
  const [peopleMap, setPeopleMap] = useState<{ [id: number]: PersonDemographics }>({})
  const [currentPerson, setCurrentPerson] = useState<null | number>(null)
  const { pathname } = useLocation()

  // Fetch people on init
  const { loading } = usePromise(async () => {
    try {
      const ids = getViewedPeople()
      if (ids.length > 0) {
        const response = await userPeopleDemographics.list({ params: { id__in: ids } })
        // Set unique and valid people
        const newMap: { [id: number]: PersonDemographics } = {}
        if (response?.data) {
          for (const person of response.data) {
            newMap[person.id] = person
          }
        }
        setPeopleMap(newMap)
      }
    } catch (_) {
      // Swallow error since this can compete with PHI access check, where failure is expected
      setPeopleMap({})
    }
  })

  // Update local state when patient profiles change, triggering side effects below.
  // Extracts the id from the location, i.e. "/patients/1618/careplans" => 1618
  const patientId = /\/patients\/(\d+)/.exec(pathname)?.[1]
  const personId = /\/people\/(\d+)/.exec(pathname)?.[1]

  // A person page names the person directly.
  const [readPathname, setReadPathname] = useState<string>()
  if (pathname !== readPathname) {
    setReadPathname(pathname)
    if (!patientId && personId) setCurrentPerson(parseInt(personId, 10))
  }

  // A patient page needs its person looked up.
  useEffect(() => {
    if (!patientId) return
    user
      .retrieve({ id: patientId })
      .then(response => {
        if (response?.data?.person?.id) setCurrentPerson(response.data.person.id)
      })
      // Swallow errors since this can compete with the PHI access check, where failure is expected
      .catch(() => undefined)
  }, [patientId])

  // Keyed on the person rather than the location, because a redirect within PatientDetail changes
  // the pathname several times. Each newly viewed person moves to the front of the list.
  const [recordedPerson, setRecordedPerson] = useState<number | null>(null)
  if (currentPerson !== recordedPerson) {
    setRecordedPerson(currentPerson)
    if (currentPerson) setIds(current => withViewedPersonFirst(current, currentPerson))
  }

  // Load a viewed person who isn't in state. An id in the list doesn't guarantee that, because
  // the list is persisted in local storage.
  const loadPersonIfMissing = useEffectEvent((id: number) => {
    if (peopleMap[id]) return
    userPeopleDemographics
      .list({ params: { id__in: [id] } })
      .then(response => {
        const person = response?.data?.[0]
        if (person) setPeopleMap(current => ({ ...current, [id]: person }))
      })
      // Swallow errors since this can compete with the PHI access check, where failure is expected
      .catch(() => undefined)
  })
  useEffect(() => {
    if (currentPerson) loadPersonIfMissing(currentPerson)
  }, [currentPerson])

  // Persist IDs in localStorage
  useEffect(() => {
    // Prevent race condition with usePromise and setting new ids
    if (!loading) saveViewedPeople(ids)
  }, [ids, loading])

  return ids.reduce((acc, currentId) => {
    if (peopleMap[currentId]) {
      acc.push(peopleMap[currentId])
    }
    return acc
  }, [] as PersonDemographics[])
}

// Moves the id to the front, adding it if it is new and trimming the list to size.
const withViewedPersonFirst = (ids: number[], id: number) => {
  const index = ids.indexOf(id)
  if (index !== -1) return moveElementInArray(ids.slice(), index, 0)
  const trimmed = ids.length > MAX_RECENTLY_VIEWED ? ids.slice(0, -1) : ids
  return [id, ...trimmed]
}

const moveElementInArray = <T>(arr: T[], fromIndex: number, toIndex: number) => {
  const moved = arr.splice(fromIndex, 1)
  arr.splice(toIndex, 0, ...moved)
  return arr
}
